import React, { useCallback, useEffect, useState } from "react";
import { Button, ConfirmDialog, Field, Input, SkeletonRows, StatGrid } from "../../../components/ui";
import { useApi } from "../../../hooks/useApi";
import { billingService } from "../../../services/billing";
import { useSession } from "../../../context/SessionContext";
import { papelLabel, planoChaveLabel, WHATSAPP_PLANO_CLINICA } from "../../../constants/billing";
import {
  ClinicaResponse, ConviteAcessoAberto, ConviteAcessoResponse, ConvitesAcessoAbertosResponse,
} from "../../../types/billing.types";
import { formatCurrency, formatDate, t } from "../../../i18n";
import { showError } from "../../../lib/feedback";
import { ITEM_CLASS } from "./LinkedList";

const money = (cents: number | null | undefined) => (cents == null ? "—" : formatCurrency(cents));

type Member = ClinicaResponse["membros"][number];

/** Só médico convidado pode ser removido (o dono não se remove; secretária é do médico). */
const MemberList: React.FC<{ members: Member[]; onRemove: (doctorId: number) => Promise<void> }> = ({ members, onRemove }) => {
  const [confirming, setConfirming] = useState<Member | null>(null);
  const [removing, setRemoving] = useState(false);

  const remove = async () => {
    if (!confirming) return;
    setRemoving(true);
    await onRemove(confirming.medico_id);
    setRemoving(false);
    setConfirming(null);
  };

  return (
    <ul className="flex flex-col gap-2.5">
      {members.map((member) => (
        <li key={member.email} className={`${ITEM_CLASS} flex-wrap justify-between`}>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-body text-fg">{member.nome || "—"}</span>
            <span className="truncate text-caption text-fg-subtle">{member.email}</span>
          </span>
          <span className="flex items-center gap-3">
            <span className="text-caption font-semibold text-fg-muted">{papelLabel(member.papel)}</span>
            {member.papel === "medico" && (
              <Button size="sm" variant="danger-ghost" onClick={() => setConfirming(member)}>{t("settings.clinic.remove")}</Button>
            )}
          </span>
        </li>
      ))}
      {confirming && (
        <ConfirmDialog
          tone="danger"
          title={t("settings.clinic.removeTitle", { name: confirming.nome || confirming.email })}
          message={t("settings.clinic.removeBody", { name: confirming.nome || confirming.email })}
          confirmLabel={t("settings.clinic.remove")}
          loading={removing}
          onConfirm={remove}
          onCancel={() => setConfirming(null)}
        />
      )}
    </ul>
  );
};

/**
 * "Minha clínica": só para o `dono` de um plano com 2+ vagas. Plano avulso
 * (1 vaga) não mostra isto: o médico é o único usuário. Deixa o responsável
 * convidar os próprios médicos sem passar pelo admin da LetsDoc.
 *
 * O link gerado é de ACESSO: quem entrar por ele vira membro desta clínica e
 * herda o plano dela, sem pagar. Por isso o backend limita quantas pessoas
 * cabem no link às vagas ainda livres (POST /billing/convites-acesso).
 *
 * Os links que ainda têm vaga ficam listados com "Copiar" até serem usados
 * ou vencerem (GET /billing/convites-acesso). Copiar de novo não gera convite
 * novo, que reservaria mais vagas.
 */
export const ClinicSection: React.FC = () => {
  const { papel, billing } = useSession();
  const clinicApi = useApi<ClinicaResponse>();
  const removeApi = useApi<{ status: string }>();
  const inviteApi = useApi<ConviteAcessoResponse>();
  const linksApi = useApi<ConvitesAcessoAbertosResponse>();

  const [clinic, setClinic] = useState<ClinicaResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [links, setLinks] = useState<ConviteAcessoAberto[]>([]);
  const [quantity, setQuantity] = useState("1");
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const isClinicPlan = billing?.modo === "clinica" || (billing?.plano?.quantidade ?? 0) > 1;
  const canSee = isClinicPlan && papel === "dono";

  const load = useCallback(async () => {
    const result = await clinicApi.request(() => billingService.getClinica(clinicApi.apiFetch), { showErrorToast: false });
    if (result) setClinic(result);

    // Falha ao listar os links não derruba a seção: só fica sem a lista.
    const open = await linksApi.request(() => billingService.getConvitesAcessoAbertos(linksApi.apiFetch), { showErrorToast: false });
    if (open) setLinks(open.convites);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!canSee) {
      setLoading(false);
      return;
    }
    load();
  }, [canSee, load]);

  const removeMember = async (doctorId: number) => {
    const result = await removeApi.request(
      () => billingService.deleteMembroClinica(removeApi.apiFetch, doctorId),
      { successMessage: t("settings.clinic.removed") },
    );
    if (result) load(); // vagas e lista mudaram
  };

  const createLink = async () => {
    const result = await inviteApi.request(
      () => billingService.postConviteAcesso(inviteApi.apiFetch, { usos_max: Number(quantity) || 1 }),
      { successMessage: t("settings.clinic.linkCreated") },
    );
    if (!result) return;
    setCopiedId(null);
    load(); // vagas livres mudaram e o link novo entra na lista
  };

  const copy = async (link: ConviteAcessoAberto) => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopiedId(link.id);
    } catch {
      showError("clipboard-failed");
    }
  };

  if (!canSee) return null;

  if (loading) {
    return <SkeletonRows label={t("settings.clinic.loading")} rows={3} />;
  }

  if (!clinic) return <p className="py-2 text-caption text-fg-subtle">{t("settings.clinic.loadError")}</p>;

  const totalSeats = clinic.vagas_totais ?? clinic.vagas_pagas;
  const billingInfo = clinic.cobranca;

  // Sem assinatura nem concessão (ex.: concessão revogada): os médicos
  // continuam na clínica no Básico, mas não há vaga nenhuma para mostrar.
  if (totalSeats === 0) {
    return (
      <div className="flex flex-col gap-3">
        <p className="rounded-control bg-warning-soft px-3 py-2.5 text-caption text-fg">
          <strong>{t("settings.clinic.basicPlan")}</strong>{" — "}
          {t("settings.clinic.basicPlanBody", { count: clinic.membros.filter((m) => m.ocupa_vaga).length })}{" "}
          <a href={WHATSAPP_PLANO_CLINICA} target="_blank" rel="noreferrer" className="font-semibold underline">
            {t("settings.clinic.talkToLetsDoc")}
          </a>.
        </p>
        <MemberList members={clinic.membros} onRemove={removeMember} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {billingInfo && (
        <section className="flex flex-col gap-2">
          <h4 className="text-caption font-semibold uppercase tracking-wide text-fg-subtle">
            {billingInfo.plano_chave ? planoChaveLabel(billingInfo.plano_chave) : t("settings.clinic.planFallback")}
          </h4>
          <StatGrid
            items={[
              { label: t("settings.clinic.contracted"), value: billingInfo.medicos_contratados },
              { label: t("settings.clinic.added"), value: billingInfo.medicos_adicionados },
              { label: t("settings.clinic.contractValue"), value: money(billingInfo.valor_contratados_centavos) },
              {
                label: t("settings.clinic.nextInvoice"),
                value: money(billingInfo.valor_proxima_fatura_centavos),
                hint: billingInfo.proxima_fatura_em ? formatDate(billingInfo.proxima_fatura_em) : undefined,
              },
            ]}
          />
          {billingInfo.valor_unitario_centavos != null && billingInfo.medicos_contratados > 0 && (
            <p className="text-caption text-fg-muted">
              {billingInfo.plano_chave?.startsWith("empresarial")
                ? t("settings.clinic.flatValue")
                : `${billingInfo.medicos_contratados} × ${money(billingInfo.valor_unitario_centavos)}`}
              {billingInfo.medicos_adicionados > 0 ? t("settings.clinic.extras", { count: billingInfo.medicos_adicionados }) : ""}
            </p>
          )}
        </section>
      )}

      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <span className="text-body text-fg">{t("settings.clinic.seats", { used: clinic.vagas_ocupadas, total: totalSeats })}</span>
          <span className="text-caption text-fg-subtle">{t("settings.clinic.seatsFree", { count: clinic.vagas_livres })}</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-primary-tint" aria-hidden>
          {/* Largura dinâmica: único valor que não cabe numa classe. */}
          <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, (clinic.vagas_ocupadas / Math.max(1, totalSeats)) * 100)}%` }} />
        </div>
      </div>

      <MemberList members={clinic.membros} onRemove={removeMember} />

      {clinic.vagas_livres <= 0 ? (
        <p className="rounded-control bg-warning-soft px-3 py-2 text-caption text-fg">{t("settings.clinic.noSeats")}</p>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <Field label={t("settings.clinic.howMany")} className="w-28">
            <Input type="number" min={1} max={clinic.vagas_livres} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </Field>
          <Button variant="primary" onClick={createLink} loading={inviteApi.loading}>{t("settings.clinic.invite")}</Button>
        </div>
      )}

      {links.length > 0 && (
        <section className="flex flex-col gap-2">
          <h4 className="text-caption font-semibold uppercase tracking-wide text-fg-subtle">{t("settings.clinic.activeLinks")}</h4>
          {links.map((link) => (
            <div key={link.id} className="rounded-control border border-primary-line bg-primary-tint px-3 py-2.5">
              <p className="text-caption text-fg-muted">
                {t("settings.clinic.linkUsage", { left: link.usos_max - link.usos, max: link.usos_max, date: formatDate(link.valido_ate) })}
              </p>
              <div className="mt-1.5 flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate text-caption text-fg">{link.url}</code>
                <Button size="sm" variant="outline" onClick={() => copy(link)}>
                  {copiedId === link.id ? t("settings.clinic.copied") : t("settings.clinic.copy")}
                </Button>
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
};
